import { Injectable } from "@angular/core";
import { HttpClient, HttpHeaders } from "@angular/common/http";
import { Observable } from "rxjs";
import { catchError } from "rxjs/operators";
import * as moment from "moment";
import { SchedulerEvent } from "src/app/shared/_devextreme/scheduler/event.model";
import { ApiBaseService } from "../../../../_api/services/api-base.service";
import { ConfigService } from "../../../../shared/services/config.service";
import { UserService } from "../../../../shared/services/user.service";
import { Period } from "../../../../shared/models/period.model";
import { SplitJobViewModel } from "src/app/shared/_devextreme/scheduler-popup/split-job.viewmodel";

@Injectable({
  providedIn: "root"
})
export class PlanningService extends ApiBaseService {

  constructor(private configService: ConfigService, private http: HttpClient, private us: UserService) {
    super(us);
    this.httpOptions = { headers: new HttpHeaders({ "Content-Type": "application/json", SessionId: this.userService.sessionId }) };
    this.baseUrl = `${this.configService.config.directUrl}/api/planning`;
  }

  getOperationsLayoutBySearch(fromDate: Date, toDate: Date, departmentIds: number[], categoryIds: number[], assignmentTypeIds: number[], operationStatusIds: number[]): Observable<any> {
    const params = `useinterval=true
      &dates=
      &from=${fromDate}
      &to=${toDate}
      &${this.getIdsAsQueryParameter(departmentIds, "departmentids")}
      &${this.getIdsAsQueryParameter(categoryIds, "categoryids")}
      &${this.getIdsAsQueryParameter(assignmentTypeIds, "assignmenttypeids")}
      &${this.getIdsAsQueryParameter(operationStatusIds, "operationstatusids")}
      &companyid=${this.userService.companyId}`;

    return this.http.get<any>(`${this.baseUrl}/getoperationslayoutbysearch?${params}`, this.httpOptions)
      .pipe(catchError(this.handleError<any>("Failed to get operations", [])));
  }

  private getIdsAsQueryParameter(array: any, param: string): string {
    if (!array) { return null; }

    let queryParameter = "";
    array.forEach((id: any) => queryParameter += `${param}=${id}${!this.isLastQueryParameterId(array, id) ? "&" : ""}` );

    return queryParameter;
  }

  private isLastQueryParameterId(array: any, id: number): boolean {
    return array.length === 1 || array[array.length - 1] === id;
  }

  getOperationsLayoutByQuickSearch(query: string): Observable<any> {
    const params = `?query=${encodeURIComponent(query)}`;

    return this.http.get<any>(`${this.baseUrl}/getoperationslayoutbyquicksearch${params}`, this.httpOptions)
      .pipe(catchError(this.handleError<any>("Failed to get operations", [])));
  }

  getPlannedJobs(selectionId: string, period: Period): Observable<any> {
    if (this.isResource(selectionId)) { return this.getPlannedJobsByResource(parseInt(selectionId.replace("r_", ""), 10), period); }
    if (this.isWorkgroup(selectionId)) { return this.getPlannedJobsByWorkgroup(parseInt(selectionId.replace("w_", ""), 10), period); }
    if (this.isDepartment(selectionId)) { return this.getPlannedJobsByDepartment(parseInt(selectionId.replace("d_", ""), 10), period); }
  }

  private isResource(selectionId: string) { return selectionId.startsWith("r_"); }
  private isWorkgroup(selectionId: string) { return selectionId.startsWith("w_"); }
  private isDepartment(selectionId: string) { return selectionId.startsWith("d_"); }

  private getPlannedJobsByResource(resourceId: number, period: Period): Observable<any> {
    const params = `?resourceid=${resourceId}&from=${moment(period.fromDate)?.format("YYYY-MM-DD")}&to=${moment(period.toDate)?.format("YYYY-MM-DD")}&shouldrecalculatejobs=false`;

    return this.http.get<any>(`${this.baseUrl}/getvmplanningbyresource${params}`, this.httpOptions)
      .pipe(catchError(this.handleError<any>("Failed to get planned jobs for selected resource", [])));
  }

  private getPlannedJobsByWorkgroup(workgroupId: number, period: Period): Observable<any> {
    const params = `?workgroupid=${workgroupId}&from=${moment(period.fromDate)?.format("YYYY-MM-DD")}&to=${moment(period.toDate)?.format("YYYY-MM-DD")}`;

    return this.http.get<any>(`${this.baseUrl}/getvmplanningbyworkgroup${params}`, this.httpOptions)
      .pipe(catchError(this.handleError<any>("Failed to get planned jobs for selected workgroup", [])));
  }

  private getPlannedJobsByDepartment(departmentId: number, period: Period): Observable<any> {
    const params = `?departmentid=${departmentId}&from=${moment(period.fromDate)?.format("YYYY-MM-DD")}&to=${moment(period.toDate)?.format("YYYY-MM-DD")}`;

    return this.http.get<any>(`${this.baseUrl}/getvmplanningbydepartment${params}`, this.httpOptions)
      .pipe(catchError(this.handleError<any>("Failed to get planned jobs for selected department", [])));
  }

  planJob(selectionId: string, event: SchedulerEvent): Observable<string> {
    if (this.isResource(selectionId)) { return this.planJobByResource(parseInt(selectionId.replace("r_", ""), 10), event); }
    if (this.isWorkgroup(selectionId)) { return this.planJobByWorkgroup(parseInt(selectionId.replace("w_", ""), 10), event); }
    if (this.isDepartment(selectionId)) { return this.planJobByDepartment(parseInt(selectionId.replace("d_", ""), 10), event); }
  }

  planJobByResource(resourceId: number, event: SchedulerEvent): Observable<string> {
    const params = `?resourceid=${resourceId}
      &operationid=${event.data.OperationId}
      &startuptime=${event.data.StartupTime}
      &processingtime=${event.data.ProcessingTime}
      &plannedstart=${moment(event.startDate)?.format("YYYY-MM-DD HH:mm")}
      &employeeid=${this.userService.account.EmployeeId}
      &parentOperationResourceId=${event.data.parentOperationResourceId}
    `;

    return this.http.post<string>(`${this.baseUrl}/planjobdirectlybyresource${params}`, this.httpOptions)
      .pipe(catchError(this.handleError<any>("Failed to plan job to resource", [])));
  }

  private planJobByWorkgroup(workgroupId: number, event: SchedulerEvent): Observable<string> {
    const params = `?workgroupid=${workgroupId}
      &operationid=${event.data.OperationId}
      &startuptime=${event.data.StartupTime}
      &processingtime=${event.data.ProcessingTime}
      &plannedstart=${moment(event.startDate)?.format("YYYY-MM-DD HH:mm")}
      &employeeid=${this.userService.account.EmployeeId}
    `;

    return this.http.post<string>(`${this.baseUrl}/planjobdirectlybyworkgroup${params}`, this.httpOptions)
      .pipe(catchError(this.handleError<any>("Failed to plan job to workgroup", [])));
  }

  private planJobByDepartment(departmentId: number, event: SchedulerEvent): Observable<string> {
    const params = `?departmentid=${departmentId}
      &operationid=${event.data.OperationId}
      &startuptime=${event.data.StartupTime}
      &processingtime=${event.data.ProcessingTime}
      &plannedstart=${moment(event.startDate)?.format("YYYY-MM-DD HH:mm")}
      &employeeid=${this.userService.account.EmployeeId}
    `;

    return this.http.post<string>(`${this.baseUrl}/planjobdirectlybydepartment${params}`, this.httpOptions)
      .pipe(catchError(this.handleError<any>("Failed to plan job to department", [])));
  }

  updateJob(event: SchedulerEvent): Observable<string> {
    const resourceId = event.priorityId && event.priorityId[0] !== event.data.ResourceId ? event.priorityId[0] : event.data.ResourceId;
    const params = `?operationresourceid=${event.data.OperationResourceId}
      &plannedstart=${moment(event.startDate)?.format("YYYY-MM-DD HH:mm")}
      &plannedstop=${moment(event.endDate)?.format("YYYY-MM-DD HH:mm")}
      &locked=${event.locked}
      &resourceId=${resourceId}
    `;

    return this.http.get<string>(`${this.baseUrl}/updateoperationresourcedirectly${params}`, this.httpOptions)
      .pipe(catchError(this.handleError<any>("Failed to update job", [])));
  }

  deleteJob(event: SchedulerEvent): Observable<string> {
    const params = `?operationresourceid=${event.data.OperationResourceId}`;

    return this.http.get<string>(`${this.baseUrl}/deleteoperationresourcedirectly${params}`, this.httpOptions)
      .pipe(catchError(this.handleError<any>("Failed to delete job", [])));
  }

  completeJob(event: SchedulerEvent, resourceActivityId: number): Observable<any> {
    const params = `?operationresourceid=${event.data.OperationResourceId}
      &resourceid=${event.data.ResourceId}
      &resourceactivityid=${resourceActivityId}
      &employeeid=${this.userService.account.EmployeeId}
    `;

    return this.http.get<any>(`${this.baseUrl}/performresourceactivity${params}`, this.httpOptions)
      .pipe(catchError(this.handleError<any>("Failed to complete job", [])));
  }

  getDependentOperations(operationId: number): Observable<any> {
    const params = `?operationid=${operationId}`;

    return this.http.get<any>(`${this.baseUrl}/getdependentoperations${params}`, this.httpOptions)
      .pipe(catchError(this.handleError<any>("Failed to get dependent operations", [])));
  }

  getOperationsByAssignment(assignmentId: number): Observable<any> {
    const params = `?assignmentid=${assignmentId}`;

    return this.http.get<any>(`${this.baseUrl}/getoperationsbyassignment${params}`, this.httpOptions)
      .pipe(catchError(this.handleError<any>("Failed to get operations on given assignment", [])));
  }

  splitJob(viewModel: SplitJobViewModel): Observable<string> {
    return this.http.post<string>(`${this.baseUrl}/splitjob`, viewModel,  this.httpOptions)
      .pipe(catchError(this.handleError<any>("Failed to split job", [])));
  }
}
